import Cookies from 'js-cookie';
import ArrowIcon from '@/components/icons/sidebar/Arrow';
import { MenuIcon, XIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/core/global-store';
import { toggleSidebar, toggleSidebarOnMobile } from '@/core/global-store/features/ui/uiSlice';
import { cn } from '@/lib/utils';
import { navigation, navigationBottom } from '@/core/global-data/SidebarData';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import NavigationDropdown from './components/sidebar/NavigationDropdown';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import usePermissions from '@/core/global-hooks/usePermissions';
import { useControlPlanePermissions } from '@/features/control-plane/hooks/useControlPlanePermissions';

const Sidebar = () => {
    const lng = Cookies.get('i18nextLng') || Cookies.get('i18next') || 'en';
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
    const isSidebarOpenOnMobile = useAppSelector((state) => state.ui.isSidebarOpenOnMobile);
    const [activeTab, setActiveTab] = useState<string>(t('dashboard'));
    const [secondaryActiveTab, setSecondaryActiveTab] = useState<string>('');
    const navigate = useNavigate();
    const user = useAppSelector((state) => state.auth.me);
    const permissions = usePermissions();
    const { me: controlPlaneMe, loading: controlPlaneLoading, isSuperAdmin } = useControlPlanePermissions();
    const hasControlPlaneAccess =
        isSuperAdmin || (controlPlaneMe?.permissions?.length ?? 0) > 0;

    // Filter navigation and navigationBottom based on permissions
    const filteredNavigation = navigation.filter((item) => {
        if ((item as { requiresControlPlaneAccess?: boolean }).requiresControlPlaneAccess) {
            // Hide until we know; never show for users with zero CP permissions.
            if (controlPlaneLoading) return false;
            return hasControlPlaneAccess;
        }
        return item.requiresPermission ? permissions.includes(item.requiresPermission) : true;
    });
    const filteredNavigationBottom = navigationBottom.filter((item) =>
        item.requiresPermission ? permissions.includes(item.requiresPermission) : true,
    );

    useEffect(() => {
        // Handle sidebar active tabs from URL updates
        const currentPath = location.pathname;
        let mainNavItem;

        if (currentPath.length > 1) {
            mainNavItem = filteredNavigation.slice(1).find((item) => {
                return currentPath.replace(/\//g, '').includes(item.to.replace(/\//g, ''));
            });
        } else {
            mainNavItem = filteredNavigation[0];
        }

        const bottomNavItem = filteredNavigationBottom.find((item) => item.to === currentPath);

        if (mainNavItem) {
            setActiveTab(t(mainNavItem.name));
            if (mainNavItem.navigation) {
                const secondNavItem = mainNavItem.navigation.find((item) => item.to === currentPath);
                setSecondaryActiveTab(secondNavItem ? t(secondNavItem.name) : '');
            }
        } else if (bottomNavItem) {
            setActiveTab(t(bottomNavItem.name));
        }
    }, [location.pathname, t, filteredNavigation, filteredNavigationBottom]);

    const toggle = () => dispatch(toggleSidebar());
    const toggleMobile = () => dispatch(toggleSidebarOnMobile());

    const handleLinkClick = () => {
        // Close sidebar on mobile when a link is clicked
        if (isSidebarOpenOnMobile) {
            toggleMobile();
        }
    };

    const handleLogout = () => {
        Cookies.remove('accessToken');
        toast.success(t('logout_success'), {
            position: 'top-right',
            duration: 3000,
        });
        navigate('/login');
        setActiveTab(t('logout'));
        handleLinkClick(); // Close sidebar on logout
    };

    return (
        <>
            {/* Mobile menu button */}
            <button
                className="md:hidden fixed top-4 end-4 z-50 p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                onClick={toggleMobile}
            >
                {isSidebarOpenOnMobile ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>

            {/* Sidebar */}
            <aside
                className={cn(
                    'fixed top-0 start-0 z-40 bg-primery-blue-500 shadow-lg transition-all duration-300 ease-in-out',
                    {
                        'w-full h-full translate-x-0': isSidebarOpenOnMobile,
                        'w-0 h-0 overflow-hidden md:overflow-visible md:w-[240px] md:h-screen md:sticky md:top-0':
                            !isSidebarOpenOnMobile && isSidebarOpen,
                        'w-0 h-0 overflow-hidden md:overflow-visible md:w-20 md:h-screen md:sticky md:top-0':
                            !isSidebarOpenOnMobile && !isSidebarOpen,
                        'translate-x-full md:translate-x-0': !isSidebarOpenOnMobile && lng === 'ar',
                        '-translate-x-full md:translate-x-0': !isSidebarOpenOnMobile && lng !== 'ar',
                    },
                )}
            >
                <div className="flex flex-col h-full text-white-blue-50">
                    {/* Logo and Toggle */}
                    <div
                        className={cn('flex items-center justify-between h-8 my-4', {
                            'mx-auto': !isSidebarOpen,
                            'ms-6': isSidebarOpen,
                        })}
                    >
                        <span className="cursor-pointer" onClick={() => navigate('/')}>
                            {isSidebarOpen || isSidebarOpenOnMobile ? (
                                <img
                                    src={user?.data.company_logo || ''}
                                    alt={user?.data.name}
                                    className="md:w-[70px] w-[60px] h-[40px]"
                                />
                            ) : (
                                <img
                                    src={user?.data.company_logo || ''}
                                    alt={user?.data.name}
                                    className="max-w-[45px] max HEK-45px] h-full w-full"
                                />
                            )}
                        </span>
                        <span></span>
                        <button
                            onClick={toggle}
                            className={cn(
                                'hidden md:block hover:text-primery-blue-500 p-2 hover:bg-gray-100 rounded-lg transition-transform',
                                {
                                    'rotate-180 !hidden': !isSidebarOpen,
                                },
                            )}
                        >
                            <ArrowIcon />
                        </button>
                    </div>

                    {/* User Info (Mobile Only) */}
                    {isSidebarOpenOnMobile && (
                        <div className="ms-6 my-4 flex items-center">
                            <img
                                src={user?.data.image || '/default-profile.png'} // Fallback image if profile_picture is not available
                                alt={user?.data.name}
                                className="w-12 h-12 rounded-full me-2 object-cover"
                            />
                            <div className="flex flex-col items-center">
                                <span className="text-[#E8EFF6] font-medium text-[14px]">{user?.data.name}</span>
                                <span className=" text-[12px] text-[#D1D1D1]">
                                    {user?.data?.role === 'owner'
                                        ? t('role_owner')
                                        : user?.data?.role === 'super_admin'
                                          ? t('role_super_admin')
                                          : user?.data.roles?.join(', ') ||
                                            user?.data.roles?.[0] ||
                                            user?.data?.role}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Navigation Items */}
                    <nav className="flex-1 ms-6 font-medium text-base leading-5 mt-2 overflow-y-auto custom-scrollbar">
                        <button
                            onClick={toggle}
                            className={cn(
                                'hidden rotate-180 hover:text-primery-blue-500 md:flex w-full mb-1 items-center justify-end py-4 px-2 hover:bg-gray-100 rounded-e-lg transition-transform',
                                {
                                    'rotate-180 !hidden': isSidebarOpen,
                                },
                            )}
                        >
                            <ArrowIcon />
                        </button>
                        {filteredNavigation.map((item, index) => {
                            const Icon = activeTab === t(item.name) ? item.activeIcon : item.icon;
                            return (
                                <div key={index}>
                                    <NavigationDropdown
                                        to={item.to}
                                        Icon={Icon}
                                        activeTab={activeTab}
                                        name={item.name}
                                        isSidebarOpen={isSidebarOpen || isSidebarOpenOnMobile}
                                        setActiveTab={setActiveTab}
                                        secondaryActiveTab={secondaryActiveTab}
                                        setSecondaryActiveTab={setSecondaryActiveTab}
                                        navigation={
                                            item.navigation
                                                ? item.navigation.filter((subItem: any) =>
                                                      subItem?.requiresPermission
                                                          ? permissions.includes(subItem.requiresPermission)
                                                          : true,
                                                  )
                                                : []
                                        }
                                        onLinkClick={handleLinkClick} // Pass the handler to NavigationDropdown
                                    />
                                </div>
                            );
                        })}
                    </nav>

                    <nav
                        className={cn('mx-5 mt-auto mb-8 font-medium text-base leading-5', {
                            'me-10 ms-0': !isSidebarOpen && !isSidebarOpenOnMobile,
                        })}
                    >
                        {filteredNavigationBottom.map((item) => {
                            const Icon = activeTab === t(item.name) ? item.activeIcon : item.icon;
                            return (
                                <NavLink
                                    key={t(item.name)}
                                    to={item.to}
                                    onClick={() => {
                                        setActiveTab(t(item.name));
                                        handleLinkClick(); // Close sidebar on link click
                                    }}
                                    className={cn(
                                        'w-full flex items-center justify-start hover:text-primery-blue-700 rounded-lg mb-1 p-2',
                                        {
                                            'bg-white-blue-50 text-primery-blue-500': activeTab === t(item.name),
                                            'hover:bg-white-blue-300': activeTab !== t(item.name),
                                            'ms-1': !isSidebarOpen && !isSidebarOpenOnMobile,
                                        },
                                    )}
                                >
                                    <Icon
                                        className={`w-6 h-6 flex items-center justify-center ${isSidebarOpen || isSidebarOpenOnMobile ? 'me-2' : 'mx-auto'}`}
                                    />
                                    <span
                                        className={`${!(isSidebarOpen || isSidebarOpenOnMobile) && 'hidden'} text-start`}
                                    >
                                        {t(item.name)}
                                    </span>
                                </NavLink>
                            );
                        })}
                        <button
                            onClick={handleLogout}
                            className={cn(
                                'w-full flex items-center justify-start hover:text-primery-blue-700 rounded-lg mb-1 p-2',
                                {
                                    'bg-white-blue-50 text-primery-blue-500': activeTab === t('logout'),
                                    'hover:bg-white-blue-300': activeTab !== t('logout'),
                                    'ms-1': !isSidebarOpen && !isSidebarOpenOnMobile,
                                },
                            )}
                        >
                            <LogOut className="pe-2" />
                            <span className={`${!(isSidebarOpen || isSidebarOpenOnMobile) && 'hidden'} text-start`}>
                                {t('logout')}
                            </span>
                        </button>
                    </nav>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
